import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc, arrayUnion } from 'firebase/firestore';
import moment from 'moment';
import { db } from '../../firebase'; // Adjust the import path as necessary
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import './transaction.css'; // Import your CSS for styling

const AddTransaction = () => {
    // State variables
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [amount, setAmount] = useState(0);
    const [description, setDescription] = useState('');
    const [date, setDate] = useState(moment().format('YYYY-MM-DD')); // Default to today's date
    const [type, setType] = useState('credit');
    const [account, setAccount] = useState('Savings'); // New state for account type
    const [userBalance, setUserBalance] = useState(0);
    const [accountBalance, setAccountBalance] = useState(0); // New state for account balance
    const [loading, setLoading] = useState(true);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // * Fetch users from Firestore on component mount
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'users'));
                const usersList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setUsers(usersList);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching users: ', error);
                setErrorMessage('Error fetching users.');
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    // * Handle user change in the dropdown
    const handleUserChange = async (e) => {
        const userId = e.target.value;
        setSelectedUserId(userId);
        try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
                const fetchedTransactions = userDoc.data().transactions || [];
                const calculatedBalance = fetchedTransactions.reduce((total, transaction) => {
                    // if transaction type is credit, add amount else subtract
                    return transaction.type === 'credit' ? total + transaction.amount : total - transaction.amount;
                }, 0);
                setUserBalance(calculatedBalance);

                const accountTransactions = fetchedTransactions.filter(txn => txn.account === account);
                const calculatedAccountBalance = accountTransactions.reduce((total, transaction) => {
                    return transaction.type === 'credit' ? total + transaction.amount : total - transaction.amount;
                }, 0);
                setAccountBalance(calculatedAccountBalance);
            }
        } catch (error) {
            console.error('Error fetching user balance: ', error);
            setErrorMessage('Error fetching user balance.');
        }
    };

    // * Handle account change
    const handleAccountChange = (e) => {
        setAccount(e.target.value);
        if (selectedUserId) {
            handleUserChange({ target: { value: selectedUserId } }); // Recalculate balance for the new account type
        }
    };

    // * Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        // make sure all fields are entered as they are required
        if (!selectedUserId || !amount || !description || !date || !type || !account) {
            setErrorMessage('All fields are required.');
            return;
        }
        if (type === 'debit' && userBalance <= 0) {
            // debits will not be successful if balance is less than zero
            setErrorMessage('Cannot perform debit transaction. Balance is zero or negative.');
            return;
        }

        try {
            // each transaction details
            const transaction = {
                amount: parseFloat(amount),
                description,
                date: moment(date).format('MMMM D, YYYY'), // Format the date to January 30, 2024 format
                type,
                account, // Add account type to transaction
            };

            await updateDoc(doc(db, 'users', selectedUserId), {
                transactions: arrayUnion(transaction),
            });

            setSuccessMessage(`Transaction added successfully! Click here to view the transaction.`);
            setTimeout(() => {
                setSuccessMessage('');
            }, 5000);

            // Update balances
            const newBalance = type === 'credit' ? userBalance + transaction.amount : userBalance - transaction.amount;
            setUserBalance(newBalance);

            const newAccountBalance = type === 'credit' ? accountBalance + transaction.amount : accountBalance - transaction.amount;
            setAccountBalance(newAccountBalance);

            // Reset form
            setAmount(0);
            setDescription('');
            setDate(moment().format('YYYY-MM-DD')); // Reset to today's date
            setType('credit');
        } catch (error) {
            console.error('Error adding transaction: ', error);
            setErrorMessage('Error adding transaction.');
        }
    };

    // Render loading or error message
    if (loading) {
        return <div>Loading...</div>;
    }

    if (errorMessage) {
        return <div>{errorMessage}</div>;
    }

    // Render form and success/error messages
    return (
        <div className="add-transaction">
            <h2>Add Transaction</h2>
            <form onSubmit={handleSubmit}>

                {/* User, Type, and Account fields */}
                <div className="container">
                    <div className="row">
                        {/* select a user */}
                        <div className="col-md-4">
                            <label>User:</label>
                            <select value={selectedUserId} onChange={handleUserChange} required>
                                <option value="">Select a user</option>
                                {users.map(user => (
                                    <option key={user.id} value={user.id}>{user.fullName}</option>
                                ))}
                            </select>
                        </div>

                        {/* select the transaction type */}
                        <div className="col-md-4">
                            <label>Type:</label>
                            <select
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                required
                            >
                                <option value="credit">Credit</option>
                                <option value="debit">Debit</option>
                            </select>
                        </div>

                        {/* select the account type */}
                        <div className="col-md-4">
                            <label>Account:</label>
                            <select
                                value={account}
                                onChange={handleAccountChange}
                                required
                            >
                                <option value="Savings">Savings</option>
                                <option value="Investments">Investments</option>
                                <option value="Loans">Loans</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Amount and Date fields */}
                <div className="container">
                    <div className="row">
                        {/* enter the transaction amount */}
                        <div className="col-md-6">
                            <label>Amount:</label>
                            <input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                required
                                min="0.01"
                                step="0.01"
                            />
                        </div>

                        {/* enter date for transaction */}
                        <div className="col-md-6">
                            <label>Date (MM/DD/YYYY):</label>
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* Description field */}
                <div>
                    <label>Description:</label>
                    <input
                        type="text"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        required
                    />
                </div>

                {/* Submit button */}
                <button type="submit" disabled={type === 'debit' && userBalance <= 0}>
                    Add Transaction
                </button>
            </form>
            
            {/* Display total balance for the selected account type */}
            {/* <div>
                <h3>Account Balance: {accountBalance.toFixed(2)}</h3>
            </div> */}

            {/* Success and error messages */}
            {successMessage && <p className="success-message">
                <Link className="Link" to={`/user/${selectedUserId}/transactions`}>{successMessage}</Link> 
                </p>}
            {errorMessage && <p className="error-message">{errorMessage}</p>}
        </div>
    );
};

export default AddTransaction;
